import { z } from 'zod';
import { blockKindSchema, cardinalitySchema, fieldTypeSchema, layoutSchema, stageCategorySchema, stageKindScopeSchema, } from '../template/manifest.js';
/**
 * Row schemas of the replicated tables of the F0 schema (docs/08 §5), as rows travel in sync
 * payloads and are stored in the client SQLite (docs/07 §4.3):
 * - identifiers are uuid strings;
 * - instants are ISO 8601 strings with offset, calendar dates are `YYYY-MM-DD`;
 * - booleans arrive as `true/false` from the API or as `0/1` from SQLite: both are accepted
 *   and normalised to booleans;
 * - JSON columns arrive as objects or as JSON text (SQLite): both are accepted and normalised.
 *
 * The API narrows these schemas per role and operation (packages/sync writable columns); the
 * clients validate rows locally with the same definitions before writing them.
 */
export const uuidSchema = z.uuid();
export const instantSchema = z.iso.datetime({ offset: true });
export const calendarDateSchema = z.iso.date();
/** Boolean as sent by the API (`true`) or by SQLite (`1`). */
export const sqlBooleanSchema = z
    .union([z.boolean(), z.literal(0), z.literal(1), z.literal('0'), z.literal('1')])
    .transform((value) => value === true || value === 1 || value === '1');
const jsonObject = z.record(z.string(), z.unknown());
/** JSON object as an object or as JSON text (how SQLite stores `jsonb` columns). */
export const jsonObjectSchema = z.union([
    jsonObject,
    z.string().transform((text, ctx) => {
        try {
            const parsed = JSON.parse(text);
            if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
                ctx.addIssue({ code: 'custom', message: 'Expected a JSON object' });
                return z.NEVER;
            }
            return parsed;
        }
        catch {
            ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });
            return z.NEVER;
        }
    }),
]);
export const workspaceKindSchema = z.enum(['personal', 'shared']);
export const membershipRoleSchema = z.enum(['owner', 'editor', 'viewer']);
export const blockStatusSchema = z.enum(['open', 'done', 'cancelled']);
export const recurrenceAnchorSchema = z.enum(['due', 'completion']);
export const syncOpSchema = z.enum(['PUT', 'PATCH', 'DELETE']);
export const rejectionReasonSchema = z.enum(['forbidden', 'validation', 'invariant_violation']);
export const taxonomyPrefixValueSchema = z.enum(['@', '#', '+']);
export const themeSchema = z.enum(['light', 'dark', 'system']);
export const densitySchema = z.enum(['comfortable', 'condensed']);
export const assetRoleSchema = z.enum(['original', 'page', 'audio', 'thumbnail', 'readable_copy']);
export const assetTextSourceSchema = z.enum(['ocr', 'pdf', 'transcript', 'article']);
export const assetUploadStatusSchema = z.enum(['pending', 'uploaded', 'failed']);
export const limitModeSchema = z.enum(['warn', 'block']);
/** JSON value (object, array or scalar) as a value or as JSON text (how SQLite stores `jsonb`). */
export const jsonValueSchema = z.union([
    z.record(z.string(), z.unknown()),
    z.array(z.unknown()),
    z.string().transform((text, ctx) => {
        try {
            return JSON.parse(text);
        }
        catch {
            ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });
            return z.NEVER;
        }
    }),
]);
const audit = {
    created_at: instantSchema,
    updated_at: instantSchema,
};
export const workspaceRowSchema = z.strictObject({
    id: uuidSchema,
    name: z.string().min(1).max(200),
    kind: workspaceKindSchema,
    owner_id: uuidSchema,
    template_key: z.string().nullable(),
    template_version: z.string().nullable(),
    ai_capture_enabled: sqlBooleanSchema,
    deleted_at: instantSchema.nullable(),
    ...audit,
});
export const membershipRowSchema = z.strictObject({
    id: uuidSchema,
    workspace_id: uuidSchema,
    user_id: uuidSchema,
    role: membershipRoleSchema,
    ...audit,
});
export const stageRowSchema = z.strictObject({
    id: uuidSchema,
    workspace_id: uuidSchema,
    key: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    category: stageCategorySchema,
    kind_scope: stageKindScopeSchema,
    color: z.string().nullable(),
    icon: z.string().nullable(),
    sort_key: z.string().min(1),
    is_capture_default: sqlBooleanSchema,
    archived_at: instantSchema.nullable(),
    ...audit,
});
export const taxonomyRowSchema = z.strictObject({
    id: uuidSchema,
    workspace_id: uuidSchema,
    key: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    prefix: taxonomyPrefixValueSchema.nullable(),
    cardinality: cardinalitySchema,
    inherited: sqlBooleanSchema,
    is_lens: sqlBooleanSchema,
    sort_key: z.string().min(1),
    ...audit,
});
export const tagRowSchema = z.strictObject({
    id: uuidSchema,
    workspace_id: uuidSchema,
    taxonomy_id: uuidSchema,
    parent_id: uuidSchema.nullable(),
    name: z.string().min(1).max(200),
    color: z.string().nullable(),
    icon: z.string().nullable(),
    user_id: uuidSchema.nullable(),
    merged_into_id: uuidSchema.nullable(),
    sort_key: z.string().min(1),
    archived_at: instantSchema.nullable(),
    deleted_at: instantSchema.nullable(),
    ...audit,
});
export const blockRowSchema = z.strictObject({
    id: uuidSchema,
    workspace_id: uuidSchema,
    parent_id: uuidSchema.nullable(),
    sort_key: z.string().min(1),
    kind: blockKindSchema,
    title: z.string().max(2000),
    body: z.string().nullable(),
    stage_id: uuidSchema.nullable(),
    stage_changed_at: instantSchema.nullable(),
    status: blockStatusSchema.nullable(),
    due_on: calendarDateSchema.nullable(),
    start_on: calendarDateSchema.nullable(),
    remind_at: instantSchema.nullable(),
    recurrence: z.string().nullable(),
    recurrence_anchor: recurrenceAnchorSchema.nullable(),
    recurrence_series_id: uuidSchema.nullable(),
    completed_at: instantSchema.nullable(),
    assignee_id: uuidSchema.nullable(),
    created_by: uuidSchema,
    capture_proposal_id: uuidSchema.nullable(),
    props: jsonObjectSchema,
    archived_at: instantSchema.nullable(),
    deleted_at: instantSchema.nullable(),
    ...audit,
});
export const blockTagRowSchema = z.strictObject({
    id: uuidSchema,
    workspace_id: uuidSchema,
    block_id: uuidSchema,
    tag_id: uuidSchema,
    taxonomy_id: uuidSchema,
    ...audit,
});
export const periodRowSchema = z.strictObject({
    id: uuidSchema,
    user_id: uuidSchema,
    started_at: instantSchema,
    closed_at: instantSchema.nullable(),
    ritual_run_id: uuidSchema.nullable(),
    summary: jsonObjectSchema.nullable(),
    ...audit,
});
export const syncRejectionRowSchema = z.strictObject({
    id: uuidSchema,
    user_id: uuidSchema,
    table_name: z.string().min(1),
    row_id: uuidSchema,
    op: syncOpSchema,
    reason_code: rejectionReasonSchema,
    message: z.string(),
    created_at: instantSchema,
    acknowledged_at: instantSchema.nullable(),
    updated_at: instantSchema,
});
export const userPreferencesRowSchema = z.strictObject({
    id: uuidSchema,
    user_id: uuidSchema,
    theme: themeSchema,
    density: densitySchema,
    locale: z.string().min(2),
    timezone: z.string().min(1),
    week_start: z.number().int().min(0).max(6),
    default_workspace_id: uuidSchema.nullable(),
    ...audit,
});
export const fieldDefRowSchema = z.strictObject({
    id: uuidSchema,
    workspace_id: uuidSchema,
    key: z.string().min(1),
    label: z.string().min(1),
    type: fieldTypeSchema,
    kind_scope: blockKindSchema.nullable(),
    options: jsonObjectSchema,
    required: sqlBooleanSchema,
    sort_key: z.string().min(1),
    archived_at: instantSchema.nullable(),
    ...audit,
});
export const fieldValueRowSchema = z.strictObject({
    id: uuidSchema,
    workspace_id: uuidSchema,
    block_id: uuidSchema,
    field_id: uuidSchema,
    value_text: z.string().nullable(),
    value_number: z.union([z.number(), z.string()]).nullable(),
    value_date: calendarDateSchema.nullable(),
    value_ts: instantSchema.nullable(),
    value_bool: sqlBooleanSchema.nullable(),
    value_ref: uuidSchema.nullable(),
    value_json: jsonValueSchema.nullable(),
    ...audit,
});
export const assetRowSchema = z.strictObject({
    id: uuidSchema,
    workspace_id: uuidSchema,
    block_id: uuidSchema.nullable(),
    capture_id: uuidSchema.nullable(),
    role: assetRoleSchema,
    storage_key: z.string().min(1),
    mime: z.string().min(1),
    bytes: z.number().int().nullable(),
    sha256: z.string().nullable(),
    phash: z.string().nullable(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    duration_ms: z.number().int().nullable(),
    thumb_asset_id: uuidSchema.nullable(),
    search_text: z.string().nullable(),
    text_source: assetTextSourceSchema.nullable(),
    upload_status: assetUploadStatusSchema,
    created_by: uuidSchema,
    deleted_at: instantSchema.nullable(),
    ...audit,
});
export const viewRowSchema = z.strictObject({
    id: uuidSchema,
    user_id: uuidSchema.nullable(),
    workspace_id: uuidSchema.nullable(),
    key: z.string().nullable(),
    name: z.string().min(1),
    icon: z.string().nullable(),
    sidebar_section: z.string().nullable(),
    sort_key: z.string().min(1),
    scope: jsonObjectSchema,
    filter: jsonObjectSchema,
    layout: layoutSchema,
    group_by: z.string().nullable(),
    sort: jsonValueSchema,
    options: jsonObjectSchema,
    is_system: sqlBooleanSchema,
    template_key: z.string().nullable(),
    deleted_at: instantSchema.nullable(),
    ...audit,
});
export const highlightTypeRowSchema = z.strictObject({
    id: uuidSchema,
    user_id: uuidSchema,
    key: z.string().min(1),
    name: z.string().min(1),
    label: z.string().min(1),
    icon: z.string().nullable(),
    color: z.string().nullable(),
    max_per_period: z.number().int().positive().nullable(),
    limit_mode: limitModeSchema,
    eligible_kind: blockKindSchema,
    eligible_stage_category: stageCategorySchema,
    pin_top: sqlBooleanSchema,
    ...audit,
});
export const highlightRowSchema = z.strictObject({
    id: uuidSchema,
    user_id: uuidSchema,
    highlight_type_id: uuidSchema,
    block_id: uuidSchema,
    period_id: uuidSchema,
    rank: z.number().int(),
    achieved_at: instantSchema.nullable(),
    removed_at: instantSchema.nullable(),
    ...audit,
});
export const ritualRowSchema = z.strictObject({
    id: uuidSchema,
    user_id: uuidSchema,
    key: z.string().min(1),
    name: z.string().min(1),
    schedule: z.string().nullable(),
    closes_period: sqlBooleanSchema,
    steps: jsonValueSchema,
    ...audit,
});
export const ritualRunRowSchema = z.strictObject({
    id: uuidSchema,
    user_id: uuidSchema,
    ritual_id: uuidSchema,
    step_state: jsonObjectSchema,
    started_at: instantSchema,
    completed_at: instantSchema.nullable(),
    ...audit,
});
/** Row schemas by table name, for generic validation (sync upload, local writes). */
export const rowSchemas = {
    user_preferences: userPreferencesRowSchema,
    workspaces: workspaceRowSchema,
    memberships: membershipRowSchema,
    stages: stageRowSchema,
    taxonomies: taxonomyRowSchema,
    tags: tagRowSchema,
    field_defs: fieldDefRowSchema,
    blocks: blockRowSchema,
    block_tags: blockTagRowSchema,
    field_values: fieldValueRowSchema,
    assets: assetRowSchema,
    views: viewRowSchema,
    highlight_types: highlightTypeRowSchema,
    rituals: ritualRowSchema,
    ritual_runs: ritualRunRowSchema,
    periods: periodRowSchema,
    highlights: highlightRowSchema,
    sync_rejections: syncRejectionRowSchema,
};
/** Tables replicated to the devices in F0, in dependency order (docs/08 §9). */
export const REPLICATED_TABLES = Object.keys(rowSchemas);
/** Column names of a row schema (including `id`). */
export function columnsOf(table) {
    return Object.keys(rowSchemas[table].shape);
}
//# sourceMappingURL=rows.js.map